import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useCreateCourse, useUpdateCourse, useTeachers } from '../../lib/hooks';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '../ui/sheet';
import { Separator } from '../ui/separator';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

const courseSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().optional(),
  level: z.string().min(1, 'Le niveau est requis'),
  schedule: z.string().optional(),
  max_students: z.number().min(1).max(100),
  teacher_id: z.string().optional(),
  status: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

const CourseForm = ({ open, onClose, course }) => {
  const isEdit = !!course;
  
  const { data: teachers } = useTeachers();
  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse(course?.id);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      name: '',
      description: '',
      level: 'A1',
      schedule: '',
      max_students: 20,
      teacher_id: '',
      status: 'active',
      start_date: '',
      end_date: '',
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        name: course.name || '',
        description: course.description || '',
        level: course.level || 'A1',
        schedule: course.schedule || '',
        max_students: course.max_students || 20,
        teacher_id: course.teacher_id || '',
        status: course.status || 'active',
        start_date: course.start_date?.split('T')[0] || '',
        end_date: course.end_date?.split('T')[0] || '',
      });
    } else {
      reset({
        name: '',
        description: '',
        level: 'A1',
        schedule: '',
        max_students: 20,
        teacher_id: '',
        status: 'active',
        start_date: '',
        end_date: '',
      });
    }
  }, [course, reset]);

  const onSubmit = async (data) => {
    try {
      // Clean up empty strings
      const cleanData = {
        ...data,
        teacher_id: data.teacher_id || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
      };
      
      if (isEdit) {
        await updateCourse.mutateAsync(cleanData);
        toast.success('Cours modifié avec succès');
      } else {
        await createCourse.mutateAsync(cleanData);
        toast.success('Cours créé avec succès');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    }
  };

  const level = watch('level');
  const status = watch('status');
  const teacherId = watch('teacher_id');

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl">
            {isEdit ? 'Modifier le cours' : 'Nouveau cours'}
          </SheetTitle>
          <SheetDescription>
            {isEdit 
              ? 'Modifiez les informations du cours' 
              : 'Remplissez les informations du nouveau cours'
            }
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-6">
          {/* Section Informations */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Informations</h3>
            
            <div className="space-y-2">
              <Label htmlFor="name">Nom du cours *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Ex: Allemand A1 - Débutants"
                className={errors.name ? 'border-destructive' : ''}
                data-testid="course-name-input"
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Description du cours..."
                rows={3}
                data-testid="course-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Niveau *</Label>
                <Select
                  value={level}
                  onValueChange={(value) => setValue('level', value)}
                >
                  <SelectTrigger data-testid="course-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="B1">B1</SelectItem>
                    <SelectItem value="B2">B2</SelectItem>
                    <SelectItem value="C1">C1</SelectItem>
                    <SelectItem value="C2">C2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={status}
                  onValueChange={(value) => setValue('status', value)}
                >
                  <SelectTrigger data-testid="course-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">À venir</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Section Planning */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Planning</h3>
            
            <div className="space-y-2">
              <Label htmlFor="schedule">Horaires</Label>
              <Input
                id="schedule"
                {...register('schedule')}
                placeholder="Ex: Lundi et Mercredi 9h-12h"
                data-testid="course-schedule-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Date de début</Label>
                <Input
                  id="start_date"
                  type="date"
                  {...register('start_date')}
                  data-testid="course-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Date de fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  {...register('end_date')}
                  data-testid="course-end-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_students">Capacité maximale</Label>
              <Input
                id="max_students"
                type="number"
                min={1}
                max={100}
                {...register('max_students', { valueAsNumber: true })}
                data-testid="course-max-students-input"
              />
            </div>
          </div>

          <Separator />

          {/* Section Enseignant */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Enseignant</h3>
            
            <div className="space-y-2">
              <Label>Enseignant assigné</Label>
              <Select
                value={teacherId || ''}
                onValueChange={(value) => setValue('teacher_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger data-testid="course-teacher-select">
                  <SelectValue placeholder="Sélectionner un enseignant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {teachers?.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <SheetFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="course-submit-btn">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                isEdit ? 'Enregistrer les modifications' : 'Créer le cours'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default CourseForm;
